// src/analytics/track.ts
// Thin wrapper over the GTM dataLayer for conversion events.
//
// Why this exists: until now nothing in the app pushed a custom event, so GA4
// had no event representing "a person asked us for something". page_view,
// session_start and first_visit were marked as key events instead, which made
// the conversion count meaningless (~3.5 "conversions" per session) and would
// have made Google Ads bidding optimise for cheap pageviews.
//
// GTM is loaded in public/index.html (GTM-5LJ3RSHZ). These events need a
// corresponding GTM trigger + GA4 event tag to reach GA4, and the GA4 key event
// must then be imported into Google Ads as the conversion action.

type LeadSource =
  | "contact_us"
  | "resources_contact"
  | "calendly_demo"
  | "ai_talent_drive_company";

type DataLayerEvent = {
  event: string;
  lead_source: LeadSource;
  form_name?: string;
  [key: string]: unknown;
};

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
  }
}

// Safe in every context we render in: the prerender pass runs these pages in a
// headless browser with googletagmanager.com blocked, so dataLayer may not
// exist. Pushing is then a no-op rather than a crash.
function push(event: DataLayerEvent) {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  } catch {
    // Analytics must never break a form submission.
  }
}

/**
 * Fire when someone successfully gives us their details — a submitted contact
 * form or a booked Calendly slot. This is the event to mark as a GA4 key event
 * and import into Google Ads.
 */
export function trackLead(source: LeadSource, formName?: string) {
  push({ event: "generate_lead", lead_source: source, form_name: formName });
}

/**
 * An intermediate funnel step, NOT a conversion. Deliberately a separate event
 * name so it can never be mistaken for a lead in GA4 or imported into Ads.
 *
 * Paired with generate_lead it exposes the drop-off between picking a slot and
 * confirming it — a gap that looks like a traffic problem but is usually a UX
 * one, and is invisible if you only measure completed bookings.
 */
export function trackFunnelStep(step: "demo_slot_selected", source: LeadSource) {
  push({ event: "funnel_step", funnel_step: step, lead_source: source });
}
